import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtUSD } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader, KpiCard } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { LineChart, DollarSign, TrendingUp } from "lucide-react";

const TYPES = ["Endowment", "Money Market", "Mutual Fund", "Bond", "CD", "Equity"];
const EMPTY = { name: "", investment_type: "Endowment", institution: "", cost_basis: "", market_value: "", purchase_date: "", restricted: "false" };

export default function Investments() {
  const { user } = useAuth();
  const canManage = ["admin", "finance_officer"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/investments").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/investments", { ...form, cost_basis: Number(form.cost_basis || 0), market_value: Number(form.market_value || 0), restricted: form.restricted === "true" });
      toast.success("Investment added"); setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (!rows) return <Loader />;
  const cb = rows.reduce((s, r) => s + (r.cost_basis || 0), 0);
  const mv = rows.reduce((s, r) => s + (r.market_value || 0), 0);
  const gain = mv - cb;

  const cols = [
    { key: "name", label: "Holding", className: "font-medium" },
    { key: "investment_type", label: "Type", render: (r) => <StatusBadge tone="blue">{r.investment_type}</StatusBadge> },
    { key: "institution", label: "Institution", className: "text-[#5C5C54]" },
    { key: "cost_basis", label: "Cost Basis", align: "right", render: (r) => fmtUSD(r.cost_basis) },
    { key: "market_value", label: "Market Value", align: "right", render: (r) => <span className="font-semibold">{fmtUSD(r.market_value)}</span> },
    { key: "unrealized_gain", label: "Unrealized", align: "right", render: (r) => <span className={r.unrealized_gain >= 0 ? "text-forest" : "text-red-600"}>{fmtUSD(r.unrealized_gain)}</span> },
    { key: "return_pct", label: "Return", align: "right", render: (r) => <span className={r.return_pct >= 0 ? "text-forest" : "text-red-600"}>{r.return_pct}%</span> },
    { key: "restricted", label: "Restricted", render: (r) => <StatusBadge tone={r.restricted ? "amber" : "gray"}>{r.restricted ? "Restricted" : "Unrestricted"}</StatusBadge> },
  ];

  return (
    <div>
      <PageHeader title="Investments & Endowment" subtitle="Track investment holdings, endowment funds and unrealized gains."
        action={canManage && <AddButton onClick={() => setOpen(true)} label="Add Investment" testid="add-invest-btn" />} />
      <div className="grid grid-cols-3 gap-6 mb-6">
        <KpiCard label="Total Cost Basis" value={fmtUSD(cb)} icon={DollarSign} />
        <KpiCard label="Total Market Value" value={fmtUSD(mv)} icon={LineChart} accent="#178070" />
        <KpiCard label="Unrealized Gain / Loss" value={fmtUSD(gain)} icon={TrendingUp} accent={gain >= 0 ? "#178070" : "#B91C1C"} />
      </div>
      <DataTable columns={cols} rows={rows} testid="investments-table" empty={{ title: "No investments recorded yet" }} />

      <Modal open={open} onOpenChange={setOpen} title="Add Investment" onSubmit={add} busy={busy} testid="invest-modal">
        <Field label="Holding Name" span={2}><TextInput required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Type"><SelectInput value={form.investment_type} onChange={(e) => setForm({ ...form, investment_type: e.target.value })} options={TYPES} /></Field>
        <Field label="Institution"><TextInput value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} /></Field>
        <Field label="Cost Basis"><TextInput type="number" required value={form.cost_basis} onChange={(e) => setForm({ ...form, cost_basis: e.target.value })} /></Field>
        <Field label="Market Value"><TextInput type="number" required value={form.market_value} onChange={(e) => setForm({ ...form, market_value: e.target.value })} /></Field>
        <Field label="Purchase Date"><TextInput type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></Field>
        <Field label="Restriction"><SelectInput value={form.restricted} onChange={(e) => setForm({ ...form, restricted: e.target.value })} options={[{ value: "false", label: "Unrestricted" }, { value: "true", label: "Restricted" }]} /></Field>
      </Modal>
    </div>
  );
}
